import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Search, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Plus,
  Users,
  FileText,
  Link2,
  UserCog,
  Filter,
  ChevronDown,
  ChevronRight,
  History,
  DollarSign,
  AlertCircle,
  Settings,
  Eye,
  Edit,
  UserPlus
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCaseManagement = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [diagnostics, setDiagnostics] = useState(null);
  const [cases, setCases] = useState([]);
  const [oficiales, setOficiales] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [plans, setPlans] = useState([]);
  
  // Search/Filter state
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOficial, setFilterOficial] = useState("all");
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [linkClientModalOpen, setLinkClientModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseHistory, setCaseHistory] = useState(null);
  
  // Form state for create case
  const [newCase, setNewCase] = useState({
    client_email: "",
    plan: "vip",
    num_people: 1,
    vendedor_email: "",
    oficial_email: "",
    payment_status: "pagado",
    notes: ""
  });
  
  const [linkClientEmail, setLinkClientEmail] = useState("");
  const [reassignOficialEmail, setReassignOficialEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [diagRes, casesRes, oficRes, vendRes, plansRes] = await Promise.all([
        axios.get(`${API}/case-management/diagnostics`),
        axios.get(`${API}/case-management/search`),
        axios.get(`${API}/case-management/oficiales`),
        axios.get(`${API}/case-management/vendedores`),
        axios.get(`${API}/case-management/plans`)
      ]);
      
      setDiagnostics(diagRes.data);
      setCases(casesRes.data.cases || []);
      setOficiales(oficRes.data.oficiales || []);
      setVendedores(vendRes.data.vendedores || []);
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error(error);
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  };

  const searchCases = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("q", search);
      if (filterPlan !== "all") params.append("plan", filterPlan);
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterOficial !== "all") params.append("oficial_id", filterOficial);
      
      const res = await axios.get(`${API}/case-management/search?${params.toString()}`);
      setCases(res.data.cases || []);
    } catch (error) {
      toast.error("Error en búsqueda");
    }
  };

  const handleCreateCase = async () => {
    if (!newCase.client_email) {
      toast.error("El email del cliente es requerido");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/case-management/create-case`, newCase);
      toast.success(res.data.message);
      setCreateModalOpen(false);
      setNewCase({
        client_email: "",
        plan: "vip",
        num_people: 1,
        vendedor_email: "",
        oficial_email: "",
        payment_status: "pagado",
        notes: ""
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creando caso");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkClient = async () => {
    if (!selectedCase || !linkClientEmail) return;
    
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/case-management/cases/${selectedCase.id}/link-client?client_email=${encodeURIComponent(linkClientEmail)}`
      );
      toast.success(res.data.message);
      setLinkClientModalOpen(false);
      setLinkClientEmail("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error vinculando cliente");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassignOficial = async () => {
    if (!selectedCase || !reassignOficialEmail) return;
    
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/case-management/cases/${selectedCase.id}/reassign-oficial?oficial_email=${encodeURIComponent(reassignOficialEmail)}`
      );
      toast.success(res.data.message);
      setReassignModalOpen(false);
      setReassignOficialEmail("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error reasignando oficial");
    } finally {
      setSubmitting(false);
    }
  };

  const loadCaseHistory = async (caseItem) => {
    setSelectedCase(caseItem);
    try {
      const res = await axios.get(`${API}/case-management/cases/${caseItem.id}/history`);
      setCaseHistory(res.data);
      setDetailModalOpen(true);
    } catch (error) {
      toast.error("Error cargando historial");
    }
  };

  const getPlanBadge = (plan) => {
    const colors = {
      vip: "bg-purple-100 text-purple-800",
      premium: "bg-blue-100 text-blue-800",
      estandar: "bg-gray-100 text-gray-800"
    };
    return colors[plan] || "bg-gray-100 text-gray-800";
  };

  const getStatusBadge = (status) => {
    const colors = {
      nuevo: "bg-green-100 text-green-800",
      activo: "bg-blue-100 text-blue-800",
      "en progreso": "bg-yellow-100 text-yellow-800",
      cerrado: "bg-gray-100 text-gray-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestión Avanzada de Casos</h1>
            <p className="text-gray-600">Panel de administración y diagnóstico</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crear Caso Manual
            </Button>
          </div>
        </motion.div>

        {/* Diagnostic Cards */}
        {diagnostics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-5 gap-4"
          >
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-600">Total Casos</span>
              </div>
              <p className="text-2xl font-bold">{diagnostics.summary?.total_cases || 0}</p>
            </div>
            
            <div className={`bg-white rounded-lg p-4 shadow-sm border ${diagnostics.summary?.orphan_cases > 0 ? 'border-orange-300' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className={`w-5 h-5 ${diagnostics.summary?.orphan_cases > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
                <span className="text-sm text-gray-600">Sin Cliente</span>
              </div>
              <p className="text-2xl font-bold">{diagnostics.summary?.orphan_cases || 0}</p>
            </div>
            
            <div className={`bg-white rounded-lg p-4 shadow-sm border ${diagnostics.summary?.missing_cases > 0 ? 'border-red-300' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <XCircle className={`w-5 h-5 ${diagnostics.summary?.missing_cases > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                <span className="text-sm text-gray-600">Clientes Sin Caso</span>
              </div>
              <p className="text-2xl font-bold">{diagnostics.summary?.missing_cases || 0}</p>
            </div>
            
            <div className={`bg-white rounded-lg p-4 shadow-sm border ${diagnostics.summary?.unassigned_cases > 0 ? 'border-yellow-300' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <UserCog className={`w-5 h-5 ${diagnostics.summary?.unassigned_cases > 0 ? 'text-yellow-500' : 'text-gray-400'}`} />
                <span className="text-sm text-gray-600">Sin Oficial</span>
              </div>
              <p className="text-2xl font-bold">{diagnostics.summary?.unassigned_cases || 0}</p>
            </div>
            
            <div className={`bg-white rounded-lg p-4 shadow-sm border ${diagnostics.summary?.payment_without_case > 0 ? 'border-red-300' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className={`w-5 h-5 ${diagnostics.summary?.payment_without_case > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                <span className="text-sm text-gray-600">Pagos Sin Caso</span>
              </div>
              <p className="text-2xl font-bold">{diagnostics.summary?.payment_without_case || 0}</p>
            </div>
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="cases" className="space-y-4">
          <TabsList>
            <TabsTrigger value="cases">Todos los Casos</TabsTrigger>
            <TabsTrigger value="issues">
              Problemas
              {(diagnostics?.summary?.missing_cases > 0 || diagnostics?.summary?.payment_without_case > 0) && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                  {(diagnostics?.summary?.missing_cases || 0) + (diagnostics?.summary?.payment_without_case || 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="unassigned">Sin Oficial ({diagnostics?.summary?.unassigned_cases || 0})</TabsTrigger>
          </TabsList>

          {/* Cases Tab */}
          <TabsContent value="cases" className="space-y-4">
            {/* Search & Filters */}
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="md:col-span-2 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por nombre, email o ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchCases()}
                    className="pl-10"
                  />
                </div>
                
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los planes</SelectItem>
                    <SelectItem value="vip">VIP</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="estandar">Estándar</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="nuevo">Nuevo</SelectItem>
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="cerrado">Cerrado</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button onClick={searchCases}>
                  <Filter className="w-4 h-4 mr-2" />
                  Filtrar
                </Button>
              </div>
            </div>

            {/* Cases Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Cliente</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Plan</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Oficial</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Estado</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Pago</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {cases.map((caseItem) => (
                      <tr key={caseItem.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">{caseItem.client_name || "Sin nombre"}</p>
                            <p className="text-sm text-gray-500">{caseItem.client_email || "Sin email"}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadge(caseItem.plan)}`}>
                            {caseItem.plan_name || caseItem.plan || "N/A"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={caseItem.assigned_official_name ? "text-gray-900" : "text-red-500"}>
                            {caseItem.assigned_official_name || "Sin asignar"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(caseItem.status)}`}>
                            {caseItem.status || "N/A"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-sm ${caseItem.payment_status === 'pagado' ? 'text-green-600' : 'text-orange-600'}`}>
                            {caseItem.payment_status || "pendiente"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => loadCaseHistory(caseItem)}
                              title="Ver detalles"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedCase(caseItem);
                                setLinkClientModalOpen(true);
                              }}
                              title="Vincular cliente"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedCase(caseItem);
                                setReassignModalOpen(true);
                              }}
                              title="Reasignar oficial"
                            >
                              <UserCog className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {cases.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No se encontraron casos</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-4">
            {/* Clients without cases */}
            {diagnostics?.missing_cases?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-4 border-b bg-red-50">
                  <h3 className="font-semibold text-red-800 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Clientes con Plan pero Sin Caso ({diagnostics.missing_cases.length})
                  </h3>
                </div>
                <div className="divide-y">
                  {diagnostics.missing_cases.map((item, idx) => (
                    <div key={idx} className="p-4 flex justify-between items-center hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{item.client_name}</p>
                        <p className="text-sm text-gray-500">{item.client_email}</p>
                        <p className="text-xs text-gray-400">Plan: {item.plan} | Pago: {item.has_payment ? "Sí" : "No"}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setNewCase({
                            ...newCase,
                            client_email: item.client_email,
                            plan: item.plan || "vip"
                          });
                          setCreateModalOpen(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Crear Caso
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payments without cases */}
            {diagnostics?.payment_without_case?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-4 border-b bg-orange-50">
                  <h3 className="font-semibold text-orange-800 flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Pagos Procesados Sin Caso ({diagnostics.payment_without_case.length})
                  </h3>
                </div>
                <div className="divide-y">
                  {diagnostics.payment_without_case.map((item, idx) => (
                    <div key={idx} className="p-4 flex justify-between items-center hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{item.client_email}</p>
                        <p className="text-sm text-gray-500">
                          Producto: {item.product_id} | Monto: ${item.amount}
                        </p>
                        <p className="text-xs text-gray-400">
                          Pagado: {new Date(item.paid_at).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          const plan = item.product_id?.replace("plan_", "") || "vip";
                          setNewCase({
                            ...newCase,
                            client_email: item.client_email,
                            plan: plan
                          });
                          setCreateModalOpen(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Crear Caso
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {diagnostics?.missing_cases?.length === 0 && diagnostics?.payment_without_case?.length === 0 && (
              <div className="bg-green-50 rounded-lg p-8 text-center border border-green-200">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p className="text-green-800 font-medium">¡Todo está en orden!</p>
                <p className="text-green-600 text-sm">No hay problemas detectados</p>
              </div>
            )}
          </TabsContent>

          {/* Unassigned Tab */}
          <TabsContent value="unassigned" className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-yellow-50">
                <h3 className="font-semibold text-yellow-800 flex items-center gap-2">
                  <UserCog className="w-5 h-5" />
                  Casos Sin Oficial Asignado
                </h3>
              </div>
              
              {diagnostics?.unassigned_cases?.length > 0 ? (
                <div className="divide-y">
                  {diagnostics.unassigned_cases.map((item, idx) => (
                    <div key={idx} className="p-4 flex justify-between items-center hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{item.client_name}</p>
                        <p className="text-sm text-gray-500">Plan: {item.plan}</p>
                        <p className="text-xs text-gray-400">ID: {item.case_id}</p>
                      </div>
                      <Select
                        onValueChange={async (oficialId) => {
                          try {
                            const oficial = oficiales.find(o => o.id === oficialId);
                            if (oficial) {
                              await axios.post(
                                `${API}/case-management/cases/${item.case_id}/reassign-oficial?oficial_email=${encodeURIComponent(oficial.email)}`
                              );
                              toast.success(`Asignado a ${oficial.name}`);
                              loadData();
                            }
                          } catch (error) {
                            toast.error("Error asignando oficial");
                          }
                        }}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Asignar oficial..." />
                        </SelectTrigger>
                        <SelectContent>
                          {oficiales.map((ofc) => (
                            <SelectItem key={ofc.id} value={ofc.id}>
                              {ofc.name} ({ofc.active_cases} casos)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <p>Todos los casos tienen oficial asignado</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Case Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Crear Caso Manual</DialogTitle>
              <DialogDescription>
                Crea un caso para un cliente existente
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Email del Cliente *</Label>
                <Input
                  placeholder="cliente@email.com"
                  value={newCase.client_email}
                  onChange={(e) => setNewCase({...newCase, client_email: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Plan</Label>
                <Select value={newCase.plan} onValueChange={(v) => setNewCase({...newCase, plan: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Número de Personas</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={newCase.num_people}
                  onChange={(e) => setNewCase({...newCase, num_people: parseInt(e.target.value) || 1})}
                />
              </div>
              
              <div>
                <Label>Estado de Pago</Label>
                <Select value={newCase.payment_status} onValueChange={(v) => setNewCase({...newCase, payment_status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pagado">Pagado</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="cortesia">Cortesía</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea
                  placeholder="Razón de la creación manual..."
                  value={newCase.notes}
                  onChange={(e) => setNewCase({...newCase, notes: e.target.value})}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateCase} disabled={submitting}>
                {submitting ? "Creando..." : "Crear Caso"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Client Modal */}
        <Dialog open={linkClientModalOpen} onOpenChange={setLinkClientModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Vincular Cliente al Caso</DialogTitle>
              <DialogDescription>
                Caso: {selectedCase?.client_name || selectedCase?.id}
              </DialogDescription>
            </DialogHeader>
            
            <div>
              <Label>Email del Cliente</Label>
              <Input
                placeholder="cliente@email.com"
                value={linkClientEmail}
                onChange={(e) => setLinkClientEmail(e.target.value)}
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkClientModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleLinkClient} disabled={submitting}>
                {submitting ? "Vinculando..." : "Vincular"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reassign Oficial Modal */}
        <Dialog open={reassignModalOpen} onOpenChange={setReassignModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reasignar Oficial</DialogTitle>
              <DialogDescription>
                Caso: {selectedCase?.client_name}
                <br />
                Oficial actual: {selectedCase?.assigned_official_name || "Sin asignar"}
              </DialogDescription>
            </DialogHeader>
            
            <div>
              <Label>Nuevo Oficial</Label>
              <Select value={reassignOficialEmail} onValueChange={setReassignOficialEmail}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar oficial..." />
                </SelectTrigger>
                <SelectContent>
                  {oficiales.map((ofc) => (
                    <SelectItem key={ofc.id} value={ofc.email}>
                      {ofc.name} ({ofc.active_cases} casos activos)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setReassignModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleReassignOficial} disabled={submitting}>
                {submitting ? "Reasignando..." : "Reasignar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Case Detail/History Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalle del Caso</DialogTitle>
            </DialogHeader>
            
            {caseHistory && (
              <div className="space-y-6">
                {/* Case Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Información del Caso</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Cliente:</span>
                      <p className="font-medium">{caseHistory.case?.client_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium">{caseHistory.case?.client_email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Plan:</span>
                      <p className="font-medium">{caseHistory.case?.plan_name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Oficial:</span>
                      <p className="font-medium">{caseHistory.case?.assigned_official_name || "Sin asignar"}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Creado:</span>
                      <p className="font-medium">{new Date(caseHistory.case?.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Método:</span>
                      <p className="font-medium">{caseHistory.created_info?.created_method || "automático"}</p>
                    </div>
                  </div>
                </div>

                {/* Payments */}
                {caseHistory.payments?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Pagos Asociados
                    </h4>
                    <div className="space-y-2">
                      {caseHistory.payments.map((pay, idx) => (
                        <div key={idx} className="bg-green-50 rounded p-3 text-sm">
                          <div className="flex justify-between">
                            <span>{pay.product_name || pay.product_id}</span>
                            <span className="font-semibold">${pay.amount}</span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(pay.paid_at || pay.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* History */}
                {caseHistory.history?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Historial de Cambios
                    </h4>
                    <div className="space-y-2">
                      {caseHistory.history.map((h, idx) => (
                        <div key={idx} className="border-l-2 border-blue-200 pl-3 py-2 text-sm">
                          <p className="font-medium">{h.action}</p>
                          {h.changes && (
                            <ul className="text-gray-600">
                              {h.changes.map((c, i) => <li key={i}>• {c}</li>)}
                            </ul>
                          )}
                          <p className="text-xs text-gray-400">
                            {h.admin_email} - {new Date(h.timestamp).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminCaseManagement;
